"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.BiometricValidation = void 0;
const web_ui_framework_1 = require("@gruposantander/web-ui-framework");
const types_1 = require("./types");
const biometricValidation_helper_1 = require("./biometricValidation.helper");
const uiInformation_1 = require("../../../uiInformation");
const ip_utility_1 = require("../../../utils/ip.utility");
class BiometricValidation {
    constructor({ verifyActions, xhr, agentToken, interactionId }) {
        this.documentType = 'user';
        this.progressId = '';
        this.biometricId = '';
        /**
         * Set new verify actions. When you do a new assisted biometric validation, you need to update the state of the new component rendered, so you need to update the verify actions
         * @param verifyActions AssistedLoginVerifyActionsType
         */
        this.updateVerifyActions = (verifyActions) => {
            this.verifyActions = verifyActions;
        };
        /**
         * Initialize validation and wait for asyncConnection resolve
         * @param username string
         */
        this.waitUserAuthorization = async (username) => {
            try {
                await this.init(username);
                await this.verifyInAppAuto();
            }
            catch (error) {
                this.verifyActions.onError({
                    code: types_1.AssistedLoginVerifyCodeType.ERROR,
                    subcode: types_1.AssistedLoginVerifySubCodeType.ERROR_INIT,
                    message: this.getResponseErrorMessage(error),
                    mode: types_1.AssistedLoginVerifyModeType.MANUAL,
                });
            }
        };
        /**
         * Check push status manually
         */
        this.manualVerify = async () => {
            await this.verifyInAppManual();
        };
        this.init = async (username) => {
            this.deviceUUID = await (0, uiInformation_1.getDeviceUUID)();
            const userIp = await (0, ip_utility_1.fetchIp)();
            const resp = await this.xhr.POST(`${window.MIDDLEWARE_AUTH_BASE_HOST}/v1/login/biometric-trusted-confirmation/init`, {
                username,
                documentType: this.documentType,
                deviceInformation: {
                    deviceUUID: this.deviceUUID,
                    webDeviceInfo: { version: navigator.userAgent },
                },
                sessionId: this.interactionId,
            }, { headers: { accessToken: this.agentToken, ['True-Client-IP']: userIp } });
            this.progressId = resp.data.progressId;
            this.biometricId = resp.data.biometricId;
        };
        this.getVerifyCallData = () => {
            const dataToSend = {
                endpoint: `${window.MIDDLEWARE_AUTH_SERVICE_HOST}/authorize/biometric-trusted-confirmation/status`,
                body: {
                    biometricId: this.biometricId,
                },
                headers: {
                    progressId: this.progressId,
                    accessToken: this.agentToken,
                },
            };
            const verifyFinalActions = {
                onError: (err, callMode = types_1.AssistedLoginVerifyModeType.MANUAL) => this.verifyActions.onError(err ?? {
                    code: types_1.AssistedLoginVerifyCodeType.ERROR,
                    subcode: types_1.AssistedLoginVerifySubCodeType.ERROR_STATUS,
                    message: this.getResponseErrorMessage(),
                    mode: callMode,
                }),
                onSuccess: async (pushStatus, callMode = types_1.AssistedLoginVerifyModeType.MANUAL) => {
                    const DENIED_CODE_RESPONSE = 403;
                    try {
                        if (pushStatus === types_1.PushStatusType.SUCCESS) {
                            const response = await this.verify();
                            this.verifyActions.onSuccess(response);
                            return true;
                        }
                    }
                    catch (err) {
                        const error = err;
                        if (error.httpStatus === DENIED_CODE_RESPONSE) {
                            this.verifyActions.onError({
                                code: types_1.AssistedLoginVerifyCodeType.DECLINED,
                                message: this.getResponseErrorMessage(),
                                mode: callMode,
                            });
                        }
                        else {
                            this.verifyActions.onError({
                                code: types_1.AssistedLoginVerifyCodeType.ERROR,
                                subcode: types_1.AssistedLoginVerifySubCodeType.ERROR_VERIFY,
                                message: this.getResponseErrorMessage(err),
                                mode: callMode,
                            });
                        }
                        this.asyncConnection?.disconnect();
                    }
                    return false;
                },
            };
            const apiCall = async () => this.xhr.POST(dataToSend.endpoint, dataToSend.body, {
                headers: dataToSend.headers,
            });
            return { verifyFinalActions, apiCall };
        };
        this.verifyInAppAuto = async () => {
            await this.asyncConnection?.disconnect();
            const { verifyFinalActions, apiCall } = this.getVerifyCallData();
            this.asyncConnection = await (0, biometricValidation_helper_1.getAsyncConnection)({ apiCall, verifyActions: verifyFinalActions });
        };
        this.verifyInAppManual = async () => {
            const { verifyFinalActions, apiCall } = this.getVerifyCallData();
            try {
                const response = await apiCall();
                const isSuccess = await verifyFinalActions.onSuccess(response.data.message);
                if (!isSuccess) {
                    verifyFinalActions.onError({
                        code: types_1.AssistedLoginVerifyCodeType.INCOMPLETE,
                        mode: types_1.AssistedLoginVerifyModeType.MANUAL,
                    });
                }
            }
            catch (error) {
                verifyFinalActions.onError();
            }
        };
        this.getRoleByResponseHeader = (responseHeaders) => {
            const xSecurityValue = responseHeaders[types_1.X_SECURITY_TOKEN];
            switch (xSecurityValue) {
                case types_1.VerifyResponseHeaderSecurity.PASSWORD_EXPIRED:
                    return web_ui_framework_1.ClientRole.EXPIRED_PASSWORD;
                case types_1.VerifyResponseHeaderSecurity.ONBOARDING:
                    return web_ui_framework_1.ClientRole.PROSPECT;
                default:
                    return web_ui_framework_1.ClientRole.CLIENT;
            }
        };
        this.verify = async () => {
            const resp = await this.xhr.POST(`${window.MIDDLEWARE_AUTH_BASE_HOST}/v1/login/biometric-trusted-confirmation/get-tokens`, {
                deviceInformation: {
                    deviceUUID: this.deviceUUID,
                    webDeviceInfo: {
                        version: navigator.userAgent,
                    },
                    sessionId: this.interactionId,
                },
            }, {
                headers: {
                    accessToken: this.agentToken,
                    progressId: this.progressId,
                },
            });
            const clientRole = this.getRoleByResponseHeader(resp.responseHeaders);
            return { ...resp.data, clientRole };
        };
        this.getResponseErrorMessage = (error) => {
            let message = '';
            if (error instanceof Error) {
                message = error.message;
            }
            return message || 'Error biometric validation';
        };
        this.xhr = xhr;
        this.verifyActions = verifyActions;
        this.agentToken = agentToken;
        this.interactionId = interactionId;
    }
}
exports.BiometricValidation = BiometricValidation;
//# sourceMappingURL=biometricValidation.js.map